import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Avatar, Button, List, Skeleton } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentsDisplay, UserHoverCard } from '@/components'
import { RequestedDepartmentFragment, UserInfoFragment, useGetOnboardingRecomUsersQuery } from '@/generated/graphql'
import { Follow, FollowAll } from '@/modules/follow'
import { ga, traceEvent } from '@/utils/ga'

import { OnboardingPageProps } from '../onboarding.interface'
import s from '../onboarding.module.less'
import { OnboardingPageLayout } from './onboarding-page-layout.component'

export const OnboardingPage6: React.FC<OnboardingPageProps> = ({ onNext, onPrevious }) => {
  const { t } = useTranslation()
  const [pageStartTime, setPageStartTime] = React.useState(moment())

  const { data, loading } = useGetOnboardingRecomUsersQuery({
    fetchPolicy: 'network-only',
    variables: {
      limit: 14,
    },
  })

  // 页面停留时间
  React.useEffect(() => {
    setPageStartTime(moment())
    return () => {
      const category = traceEvent.onboarding
      ga('event', category.events.expose_time_page, {
        event_category: category.event_category,
        event_label: '6',
        value: moment().diff(pageStartTime, 'ms'),
      })
    }
  }, [])

  const users = data?.recommend.onboardingRecommendedUsers ?? []

  const subHeader = t('onboarding.page6.subHeader')

  const userIds = users.map((u) => u.userId)

  const getUserList = (users: (UserInfoFragment & { departments: RequestedDepartmentFragment[] })[], style?: any) => {
    return users.length > 0 ? (
      <div className={s.page6}>
        <List
          dataSource={users}
          loading={loading}
          split={false}
          itemLayout='horizontal'
          size='small'
          style={style}
          className={s.list}
          renderItem={(u) => (
            <List.Item
              key={u.userId}
              style={{ paddingLeft: 0, paddingRight: 0 }}
              actions={[
                <Follow
                  display='icon'
                  key='follow'
                  isOnboarding={true}
                  followeeUserId={u.userId}
                  isFollowing={u.isFollowing}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<Avatar src={u.avatarUrl} />}
                title={<UserHoverCard email={u.email}>{u.name}</UserHoverCard>}
                description={<DepartmentsDisplay departments={u.departments} />}
              />
            </List.Item>
          )}
        />
      </div>
    ) : null
  }

  const leftUsers = users.slice(0, 7)
  const rightUsers = users.slice(7, 14)
  const rightUserExists = rightUsers.length > 0

  const left = getUserList(leftUsers, rightUserExists ? { marginRight: 12 } : undefined)
  const right = getUserList(users.slice(8, 16), rightUserExists ? { marginLeft: 12 } : undefined)

  const footer = (
    <>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_prev_page, {
            event_category: category.event_category,
            event_label: '6',
          })
          onPrevious!()
        }}
      >
        <LeftOutlined />
        {t('onboarding.previous')}
      </Button>
      <Button
        type='primary'
        onClick={() => {
          const category = traceEvent.onboarding
          ga('event', category.events.click_next_page, {
            event_category: category.event_category,
            event_label: '6',
          })
          onNext!()
        }}
      >
        {t('onboarding.next')}
        <RightOutlined />
      </Button>
    </>
  )

  return (
    <OnboardingPageLayout
      header='DISCOVER PEOPLE'
      subHeader={subHeader}
      subHeaderAction={loading ? <Skeleton.Button /> : <FollowAll followeeIds={userIds} target='user' />}
      left={left}
      right={right}
      footer={footer}
      percent={6}
    />
  )
}
